import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import type { User, CleanlinessActivity, AdminAction, BugReport } from '../types';
import { ComparisonSlider } from './BeforeAfterCamera';
import { sessionService } from '../services/sessionService';
import { initializeGeminiService, getGeminiService } from '../services/geminiService';
import './AdminPanel.css';

interface AdminPanelProps {
  onNavigate: (page: string) => void;
  showToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

// Component to display multiple photo pairs in admin panel
const AdminMultiPhotoDisplay: React.FC<{
  post: CleanlinessActivity;
}> = ({ post }) => {
  const [photoPairs, setPhotoPairs] = useState<Array<{
    beforeUrl: string;
    afterUrl: string;
    orderIndex: number;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Reset currentIndex when post changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [post.id]);

  useEffect(() => {
    // Load photo pairs from session if available
    if (post.sessionId) {
      setLoading(true);
      // Get photo pairs directly from session, not from post
      sessionService.getSessionPhotoPairs(post.sessionId).then(pairs => {
        
        if (pairs.length > 0) {
          const mapped = pairs.map((pair, idx) => ({
            beforeUrl: pair.beforePhoto?.storage_url || '',
            afterUrl: pair.afterPhoto?.storage_url || '',
            orderIndex: idx
          }));
          setPhotoPairs(mapped);
        } else {
          // Fallback to single photo if no pairs
          setPhotoPairs([{
            beforeUrl: (post as any).before_image || (post as any).before_photo_url || '',
            afterUrl: (post as any).after_image || (post as any).after_photo_url || '',
            orderIndex: 0
          }]);
        }
        setLoading(false);
      }).catch(err => {
        // Don't fall back to single photo on error
        setPhotoPairs([]);
        setLoading(false);
      });
    } else if ((post as any).before_image && (post as any).after_image) {
      // Single photo pair for backward compatibility
      setPhotoPairs([{
        beforeUrl: (post as any).before_image || (post as any).before_photo_url || '',
        afterUrl: (post as any).after_image || (post as any).after_photo_url || '',
        orderIndex: 0
      }]);
    }
  }, [post.id, post.sessionId]); // Dependencies exactly like ActivityFeed

  if (loading) {
    return <p>Loading photos...</p>;
  }

  if (photoPairs.length === 0) {
    return post.imageUrl ? <img src={post.imageUrl} alt="Activity" className="post-image" /> : null;
  }

  const currentPair = photoPairs[currentIndex];

  return (
    <div style={{ marginTop: '1rem' }}>
      <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#333' }}>Cleaning Progress ({photoPairs.length} {photoPairs.length === 1 ? 'pair' : 'pairs'})</h4>
      {photoPairs.length > 1 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1rem',
            padding: '0.5rem',
            background: '#f8f9fa',
            borderRadius: '0.5rem'
          }}>
            <button 
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} 
              disabled={currentIndex === 0}
              style={{ 
                padding: '0.5rem 1rem',
                background: currentIndex === 0 ? '#e9ecef' : '#3b82f6',
                color: currentIndex === 0 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              ← Previous
            </button>
            <span style={{ fontWeight: '600', fontSize: '1rem', color: '#555' }}>
              Pair {currentIndex + 1} of {photoPairs.length}
            </span>
            <button 
              onClick={() => setCurrentIndex(Math.min(photoPairs.length - 1, currentIndex + 1))} 
              disabled={currentIndex === photoPairs.length - 1}
              style={{ 
                padding: '0.5rem 1rem',
                background: currentIndex === photoPairs.length - 1 ? '#e9ecef' : '#3b82f6',
                color: currentIndex === photoPairs.length - 1 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: currentIndex === photoPairs.length - 1 ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              Next →
            </button>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            justifyContent: 'center',
            padding: '0.75rem',
            background: '#f8f9fa',
            borderRadius: '0.5rem'
          }}>
            {photoPairs.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                style={{
                  width: idx === currentIndex ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  border: 'none',
                  background: idx === currentIndex ? '#3b82f6' : '#dee2e6',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.3s ease'
                }}
                aria-label={`Go to pair ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      )}
      <div style={{ 
        background: '#f0f0f0', 
        padding: '1rem', 
        borderRadius: '0.5rem',
        minHeight: '300px',
        position: 'relative'
      }}>
        {currentPair.beforeUrl && currentPair.afterUrl ? (
          <ComparisonSlider
            beforeImage={currentPair.beforeUrl}
            afterImage={currentPair.afterUrl}
            pairNumber={currentIndex + 1}
            totalPairs={photoPairs.length}
          />
        ) : (
        <div style={{ display: 'flex', gap: '1rem' }}>
          {currentPair.beforeUrl && (
            <div style={{ flex: 1 }}>
              <p style={{ marginBottom: '0.5rem' }}>Before:</p>
              <img src={currentPair.beforeUrl} alt="Before" style={{ width: '100%', borderRadius: '0.5rem' }} />
            </div>
          )}
          {currentPair.afterUrl && (
            <div style={{ flex: 1 }}>
              <p style={{ marginBottom: '0.5rem' }}>After:</p>
              <img src={currentPair.afterUrl} alt="After" style={{ width: '100%', borderRadius: '0.5rem' }} />
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

const AdminPanel: React.FC<AdminPanelProps> = ({ onNavigate, showToast }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'posts' | 'bugs' | 'rewards' | 'stats' | 'logs'>('users');
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [pendingPosts, setPendingPosts] = useState<CleanlinessActivity[]>([]);
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [rewardRedemptions, setRewardRedemptions] = useState<any[]>([]);
  const [adminActions, setAdminActions] = useState<AdminAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingUsers: 0,
    approvedUsers: 0,
    totalPosts: 0,
    pendingPosts: 0,
    approvedPosts: 0,
    totalPoints: 0,
    openBugs: 0,
    totalBugs: 0,
    pendingRedemptions: 0,
    totalRedemptions: 0,
  });

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedPost, setSelectedPost] = useState<CleanlinessActivity | null>(null);
  const [selectedBug, setSelectedBug] = useState<BugReport | null>(null);
  const [selectedRedemption, setSelectedRedemption] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [allocatedPoints, setAllocatedPoints] = useState(0);
  const [adminComment, setAdminComment] = useState('');
  const [bugAdminNotes, setBugAdminNotes] = useState('');
  const [bugStatus, setBugStatus] = useState<'open' | 'in_progress' | 'resolved' | 'closed'>('open');
  const [redemptionStatus, setRedemptionStatus] = useState<'pending' | 'approved' | 'rejected' | 'delivered'>('pending');
  const [redemptionNotes, setRedemptionNotes] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // AI Analysis states
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAiInsights, setShowAiInsights] = useState(false);

  useEffect(() => {
    // Initialize Gemini service with built-in key
    initializeGeminiService();
  }, []);

  useEffect(() => {
    if (user?.isAdmin) {
      loadAdminData();
      
      // Set up auto-refresh every 10 seconds
      const interval = setInterval(() => {
        loadAdminData(true);
      }, 10000); // 10 seconds

      // Clean up interval on unmount
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadAdminData = async (isAutoRefresh = false) => {
    if (isAutoRefresh) {
      setIsRefreshing(true);
    } else if (!hasInitialLoad) {
      setLoading(true);
    }
    
    try {
      await Promise.all([
        loadPendingUsers(),
        loadPendingPosts(),
        loadBugReports(),
        loadRewardRedemptions(),
        loadAdminActions(),
        loadStats(),
      ]);
      setLastRefreshTime(new Date());
      
      if (!hasInitialLoad) {
        setHasInitialLoad(true);
      }
    } catch (error) {
    }
    
    if (isAutoRefresh) {
      setIsRefreshing(false);
    } else {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (!error && data) {
      // Only update if data has actually changed
      setPendingUsers(data.map(profile => ({
        id: profile.id,
        rollNo: profile.roll_no,
        name: profile.name,
        department: profile.department,
        email: profile.email,
        points: profile.points,
        level: 1,
        joinedAt: new Date(profile.created_at),
        rank: profile.rank,
        achievements: [],
        streak: profile.streak,
        lastActivityDate: profile.last_activity_date ? new Date(profile.last_activity_date) : null,
        lastLoginDate: profile.last_login_date ? new Date(profile.last_login_date) : null,
        totalActivities: profile.total_activities,
        beforeAfterCount: profile.before_after_count,
        videoCount: profile.video_count,
        loginStreak: profile.login_streak,
        status: profile.status,
        isAdmin: profile.is_admin,
      })));
    }
  };

  const loadPendingPosts = async () => {
    // First get the activities
    const { data: activities, error: activitiesError } = await supabase
      .from('activities')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (activitiesError) {
      return;
    }

    if (!activities || activities.length === 0) {
      setPendingPosts([]);
      return;
    }

    // Get unique profile IDs
    const profileIds = [...new Set(activities.map(a => a.profile_id))];
    
    // Fetch profiles separately
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, name, avatar_url')
      .in('id', profileIds);

    if (profilesError) {
    }

    // Create a map of profile ID to profile data
    const profileMap = new Map(profiles?.map(p => [p.id, { name: p.name, avatar_url: p.avatar_url }]) || []);

    // Map activities to the expected format
    setPendingPosts(activities.map(activity => {
      const profileData = profileMap.get(activity.profile_id);
      return {
        id: activity.id,
        userId: activity.profile_id,
        userName: profileData?.name || 'Unknown User',
        userAvatar: profileData?.avatar_url,
        type: activity.activity_type || activity.type || 'before-after',
        title: activity.title || 'Cleaning Activity',
        description: activity.description || activity.caption || '',
        points: activity.points_earned || activity.points_awarded || 0,
        status: activity.status,
        createdAt: new Date(activity.created_at),
        imageUrl: activity.before_image || activity.after_image || activity.video_url || '',
        location: activity.location || 'Unknown Location',
        sessionId: activity.session_id,
        before_image: activity.before_image,
        after_image: activity.after_image,
        before_photo_url: activity.before_photo_url,
        after_photo_url: activity.after_photo_url,
      };
    }));
  };

  const loadBugReports = async () => {
    // First get bug reports
    const { data: bugs, error } = await supabase
      .from('bug_reports')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      return;
    }

    if (!bugs || bugs.length === 0) {
      setBugReports([]);
      return;
    }

    // Get unique user IDs
    const userIds = [...new Set(bugs.map(b => b.user_id))];
    
    // Fetch user names separately
    const { data: users } = await supabase
      .from('profiles')
      .select('id, name')
      .in('id', userIds);

    // Create a map of user ID to name
    const userMap = new Map(users?.map(u => [u.id, u.name]) || []);

    // Map bugs with user names
    setBugReports(bugs.map(bug => ({
      id: bug.id,
      userId: bug.user_id,
      userName: userMap.get(bug.user_id) || 'Unknown User',
      title: bug.title,
      description: bug.description,
      category: bug.category,
      priority: bug.priority,
      status: bug.status,
      screenshotUrl: bug.screenshot_url,
      browserInfo: bug.browser_info,
      createdAt: new Date(bug.created_at),
      updatedAt: new Date(bug.updated_at),
      resolvedAt: bug.resolved_at ? new Date(bug.resolved_at) : undefined,
      resolvedBy: bug.resolved_by,
      adminNotes: bug.admin_notes,
    })));
  };

  const loadRewardRedemptions = async () => {
    // First get redemptions
    const { data: redemptions, error } = await supabase
      .from('reward_redemptions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      return;
    }

    if (!redemptions || redemptions.length === 0) {
      setRewardRedemptions([]);
      return;
    }

    // Get unique user IDs
    const userIds = [...new Set(redemptions.map(r => r.user_id))];
    
    // Fetch user names separately
    const { data: users } = await supabase
      .from('profiles')
      .select('id, name, roll_no')
      .in('id', userIds);

    // Create a map of user ID to user info
    const userMap = new Map(users?.map(u => [u.id, { name: u.name, rollNo: u.roll_no }]) || []);

    // Map redemptions with user names
    setRewardRedemptions(redemptions.map(redemption => ({
      id: redemption.id,
      userId: redemption.user_id,
      userName: userMap.get(redemption.user_id)?.name || 'Unknown User',
      userRollNo: userMap.get(redemption.user_id)?.rollNo || 'Unknown',
      rewardName: redemption.reward_name,
      pointsCost: redemption.points_cost,
      whatsappNumber: redemption.whatsapp_number,
      status: redemption.status,
      createdAt: new Date(redemption.created_at),
      processedAt: redemption.processed_at ? new Date(redemption.processed_at) : undefined,
      processedBy: redemption.processed_by,
      adminNotes: redemption.admin_notes,
      rejectionReason: redemption.rejection_reason,
    })));
  };

  const loadAdminActions = async () => {
    const { data, error } = await supabase
      .from('admin_actions')
      .select('*, profiles(name)')
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) {
      setAdminActions(data.map(action => ({
        id: action.id,
        adminId: action.admin_id,
        adminName: action.profiles?.name,
        actionType: action.action_type,
        targetId: action.target_id,
        targetType: action.target_type,
        reason: action.reason,
        pointsAllocated: action.points_allocated,
        createdAt: new Date(action.created_at),
      })));
    }
  };

  const loadStats = async () => {
    try {
      const [
        { count: totalUsers },
        { count: pendingUsersCount },
        { count: approvedUsers },
        { count: totalPosts },
        { count: pendingPostsCount },
        { count: approvedPosts },
        { data: pointsData },
        { count: openBugsCount },
        { count: totalBugsCount },
        { count: pendingRedemptionsCount },
        { count: totalRedemptionsCount },
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
        supabase.from('activities').select('*', { count: 'exact', head: true }),
        supabase.from('activities').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('activities').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
        supabase.from('profiles').select('points'),
        supabase.from('bug_reports').select('*', { count: 'exact', head: true }).in('status', ['open', 'in_progress']),
        supabase.from('bug_reports').select('*', { count: 'exact', head: true }),
        supabase.from('reward_redemptions').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('reward_redemptions').select('*', { count: 'exact', head: true }),
      ]);

      const totalPoints = pointsData?.reduce((sum, profile) => sum + (profile.points || 0), 0) || 0;

      setStats({
        totalUsers: totalUsers || 0,
        pendingUsers: pendingUsersCount || 0,
        approvedUsers: approvedUsers || 0,
        totalPosts: totalPosts || 0,
        pendingPosts: pendingPostsCount || 0,
        approvedPosts: approvedPosts || 0,
        totalPoints,
        openBugs: openBugsCount || 0,
        totalBugs: totalBugsCount || 0,
        pendingRedemptions: pendingRedemptionsCount || 0,
        totalRedemptions: totalRedemptionsCount || 0,
      });
    } catch (error) {
    }
  };

  const handleApproveUser = async () => {
    if (!selectedUser || !user) return;

    try {
      const { data, error } = await supabase.rpc('approve_user', {
        user_id: selectedUser.id
      });

      if (error) {
        if (showToast) {
          showToast(`Failed to approve user: ${error.message}`, 'error');
        }
        return;
      }

      if (showToast) {
        showToast('User approved successfully!', 'success');
      }
      setShowUserModal(false);
      setSelectedUser(null);
      loadAdminData();
    } catch (error) {
      if (showToast) {
        showToast('Failed to approve user', 'error');
      }
    }
  };

  const handleRejectUser = async () => {
    if (!selectedUser || !user || !rejectionReason) return;

    try {
      const { data, error } = await supabase.rpc('reject_user', {
        user_id: selectedUser.id,
        reason: rejectionReason
      });

      if (error) {
        if (showToast) {
          showToast(`Failed to reject user: ${error.message}`, 'error');
        }
        return;
      }

      if (showToast) {
        showToast('User rejected', 'info');
      }
      setShowUserModal(false);
      setSelectedUser(null);
      setRejectionReason('');
      loadAdminData();
    } catch (error) {
      if (showToast) {
        showToast('Failed to reject user', 'error');
      }
    }
  };

  const analyzePostWithAI = async (post: CleanlinessActivity) => {
    if (!post) {
      return;
    }
    
    setIsAnalyzing(true);
    setAiAnalysis(null);
    
    try {
      let photoPairs: Array<{ beforeUrl: string; afterUrl: string; orderIndex: number }> = [];
      
      if (post.sessionId) {
        // Get photo pairs from session
        const pairs = await sessionService.getSessionPhotoPairs(post.sessionId);
        if (pairs.length > 0) {
          photoPairs = pairs.map((pair, idx) => ({
            beforeUrl: pair.beforePhoto?.storage_url || '',
            afterUrl: pair.afterPhoto?.storage_url || '',
            orderIndex: idx
          })).filter(pair => pair.beforeUrl && pair.afterUrl);
        }
      } else {
        // Single photo pair (legacy)
        const beforeUrl = (post as any).before_image || (post as any).before_photo_url || '';
        const afterUrl = (post as any).after_image || (post as any).after_photo_url || '';
        
        if (beforeUrl && afterUrl) {
          photoPairs = [{
            beforeUrl,
            afterUrl,
            orderIndex: 0
          }];
        }
      }
      
      if (photoPairs.length === 0) {
        throw new Error('No valid photo pairs found');
      }
      
      // Call Gemini service
      const geminiService = getGeminiService();
      const analysis = await geminiService.analyzeActivity(
        photoPairs,
        post.type || 'Campus improvement',
        post.location,
        post.description,
        post.userName || 'Unknown user'
      );
      
      setAiAnalysis(analysis);
      setShowAiInsights(true);
      
      // Auto-suggest points based on AI analysis
      if (analysis.overallAssessment.suggestedPoints) {
        setAllocatedPoints(analysis.overallAssessment.suggestedPoints.recommended);
      }
    } catch (error) {
      console.error('AI analysis failed:', error);
      if (showToast) {
        showToast('AI analysis failed. You can still review manually.', 'error');
      }
    }
    
    setIsAnalyzing(false);
  };

  const handleApprovePost = async () => {
    if (!selectedPost || !user || allocatedPoints <= 0) return;

    try {
      
      const { data, error } = await supabase.rpc('approve_activity', {
        activity_id: selectedPost.id,
        points_to_award: allocatedPoints,
        comment: adminComment || null
      });

      if (error) {
        if (showToast) {
          showToast(`Failed to approve post: ${error.message}`, 'error');
        }
        return;
      }

      if (showToast) {
        showToast('Post approved successfully!', 'success');
      }
      setShowPostModal(false);
      setSelectedPost(null);
      setAllocatedPoints(0);
      setAdminComment('');
      loadAdminData();
    } catch (error) {
      if (showToast) {
        showToast('Failed to approve post', 'error');
      }
    }
  };


  const handleRejectPost = async () => {
    if (!selectedPost || !user || !rejectionReason) return;

    try {
      const { data, error } = await supabase.rpc('reject_activity', {
        activity_id: selectedPost.id,
        reason: rejectionReason
      });

      if (error) {
        if (showToast) {
          showToast(`Failed to reject post: ${error.message}`, 'error');
        }
        return;
      }

      if (showToast) {
        showToast('Post rejected', 'info');
      }
      setShowPostModal(false);
      setSelectedPost(null);
      setRejectionReason('');
      loadAdminData();
    } catch (error) {
      if (showToast) {
        showToast('Failed to reject post', 'error');
      }
    }
  };

  const handleUpdateBugReport = async () => {
    if (!selectedBug || !user) return;

    try {
      const updates: any = {
        status: bugStatus,
        admin_notes: bugAdminNotes,
      };

      if (bugStatus === 'resolved' || bugStatus === 'closed') {
        updates.resolved_at = new Date().toISOString();
        updates.resolved_by = user.id;
      }

      const { error } = await supabase
        .from('bug_reports')
        .update(updates)
        .eq('id', selectedBug.id);

      if (error) {
        if (showToast) {
          showToast(`Failed to update bug report: ${error.message}`, 'error');
        }
        return;
      }

      if (showToast) {
        showToast('Bug report updated successfully!', 'success');
      }
      setShowBugModal(false);
      setSelectedBug(null);
      setBugAdminNotes('');
      setBugStatus('open');
      loadAdminData();
    } catch (error) {
      if (showToast) {
        showToast('Failed to update bug report', 'error');
      }
    }
  };

  const handleProcessRedemption = async () => {
    if (!selectedRedemption || !user) return;

    try {
      const { data, error } = await supabase.rpc('process_redemption', {
        p_redemption_id: selectedRedemption.id,
        p_status: redemptionStatus,
        p_notes: redemptionNotes || null,
        p_rejection_reason: redemptionStatus === 'rejected' ? redemptionNotes : null
      });

      if (error) {
        if (showToast) {
          showToast(`Failed to process redemption: ${error.message}`, 'error');
        }
        return;
      }

      if (showToast) {
        showToast('Redemption processed successfully!', 'success');
      }
      setShowRedemptionModal(false);
      setSelectedRedemption(null);
      setRedemptionNotes('');
      setRedemptionStatus('pending');
      loadAdminData();
    } catch (error) {
      if (showToast) {
        showToast('Failed to process redemption', 'error');
      }
    }
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-panel">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have admin privileges to access this panel.</p>
          <button onClick={() => onNavigate('dashboard')} className="back-btn">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <button onClick={() => onNavigate('dashboard')} className="back-btn">
          ← Back
        </button>
        <h1>Admin Panel</h1>
        <div className="refresh-info">
          {isRefreshing && <span className="refreshing-indicator">🔄 Refreshing...</span>}
          <span className="last-refresh">Last updated: {lastRefreshTime.toLocaleTimeString()}</span>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <span className="tab-icon">👥</span>
          Users
          {stats.pendingUsers > 0 && <span className="badge">{stats.pendingUsers}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          <span className="tab-icon">📝</span>
          Posts
          {stats.pendingPosts > 0 && <span className="badge">{stats.pendingPosts}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'bugs' ? 'active' : ''}`}
          onClick={() => setActiveTab('bugs')}
        >
          <span className="tab-icon">🐛</span>
          Bugs/Suggestions
          {stats.openBugs > 0 && <span className="badge">{stats.openBugs}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
        >
          <span className="tab-icon">🎁</span>
          Rewards
          {stats.pendingRedemptions > 0 && <span className="badge">{stats.pendingRedemptions}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <span className="tab-icon">📊</span>
          Statistics
        </button>
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          <span className="tab-icon">📜</span>
          Logs
        </button>
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="loading">Loading admin data...</div>
        ) : (
          <>
            {activeTab === 'users' && (
              <div className="users-section">
                <h2>Pending User Approvals</h2>
                {pendingUsers.length === 0 ? (
                  <p className="empty-state">No pending users</p>
                ) : (
                  <div className="user-list">
                    {pendingUsers.map(user => (
                      <div key={user.id} className="user-card">
                        <div className="user-info">
                          <h3>{user.name}</h3>
                          <p>Roll No: {user.rollNo}</p>
                          <p>Department: {user.department}</p>
                          <p>Email: {user.email}</p>
                          <p className="timestamp">
                            Joined: {user.joinedAt.toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserModal(true);
                          }}
                          className="review-btn"
                        >
                          Review
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'posts' && (
              <div className="posts-section">
                <h2>Pending Post Approvals</h2>
                {pendingPosts.length === 0 ? (
                  <p className="empty-state">No pending posts</p>
                ) : (
                  <div className="post-list">
                    {pendingPosts.map(post => (
                      <div key={post.id} className="post-card">
                        <div className="post-info">
                          <div className="post-header">
                            <div className="post-user">
                              {post.userAvatar ? (
                                <img src={post.userAvatar} alt={post.userName} className="post-user-avatar" />
                              ) : (
                                <div className="post-user-avatar">{post.userName?.charAt(0).toUpperCase()}</div>
                              )}
                              <div>
                                <h3>{post.title}</h3>
                                <p className="post-author">By: {post.userName}</p>
                              </div>
                            </div>
                          </div>
                          <p className="post-type">Type: {post.type}</p>
                          <p className="post-location">Location: {post.location}</p>
                          <p>{post.description}</p>
                          {post.sessionId && (
                            <p style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                              📸 Has session photos
                            </p>
                          )}
                          <p className="timestamp">
                            Submitted: {post.createdAt.toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedPost(post);
                            setAllocatedPoints(10); // Default points
                            setShowPostModal(true);
                            setAiAnalysis(null);
                            setShowAiInsights(false);
                            // Automatically start AI analysis
                          }}
                          className="review-btn"
                        >
                          Review
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'bugs' && (
              <div className="bugs-section">
                <h2>Bug Reports & Suggestions</h2>
                {bugReports.length === 0 ? (
                  <p className="empty-state">No bug reports or suggestions yet</p>
                ) : (
                  <div className="bug-list">
                    {bugReports.map(bug => (
                      <div key={bug.id} className="bug-card">
                        <div className="bug-header">
                          <h3>{bug.title}</h3>
                          <div className="bug-badges">
                            <span className={`priority-badge priority-${bug.priority}`}>
                              {bug.priority}
                            </span>
                            <span className={`status-badge status-${bug.status}`}>
                              {bug.status}
                            </span>
                            <span className="category-badge">
                              {bug.category}
                            </span>
                          </div>
                        </div>
                        <div className="bug-info">
                          <p className="bug-author">By: {bug.userName}</p>
                          <p className="bug-description">{bug.description}</p>
                          <p className="timestamp">
                            Reported: {bug.createdAt.toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedBug(bug);
                            setBugStatus(bug.status);
                            setBugAdminNotes(bug.adminNotes || '');
                            setShowBugModal(true);
                          }}
                          className="review-btn"
                        >
                          Review
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'rewards' && (
              <div className="rewards-section">
                <h2>Reward Redemptions</h2>
                {rewardRedemptions.length === 0 ? (
                  <p className="empty-state">No reward redemptions yet</p>
                ) : (
                  <div className="redemptions-list">
                    {rewardRedemptions.map(redemption => (
                      <div key={redemption.id} className="redemption-card">
                        <div className="redemption-header">
                          <h3>{redemption.rewardName}</h3>
                          <div className="redemption-badges">
                            <span className={`status-badge status-${redemption.status}`}>
                              {redemption.status}
                            </span>
                            <span className="points-badge">
                              {redemption.pointsCost} pts
                            </span>
                          </div>
                        </div>
                        <div className="redemption-info">
                          <p className="redemption-user">
                            <strong>User:</strong> {redemption.userName} ({redemption.userRollNo})
                          </p>
                          <p className="redemption-whatsapp">
                            <strong>WhatsApp:</strong> {redemption.whatsappNumber}
                          </p>
                          <p className="redemption-time">
                            <strong>Requested:</strong> {redemption.createdAt.toLocaleDateString()} at {redemption.createdAt.toLocaleTimeString()}
                          </p>
                          {redemption.processedAt && (
                            <p className="redemption-processed">
                              <strong>Processed:</strong> {redemption.processedAt.toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setSelectedRedemption(redemption);
                            setRedemptionStatus(redemption.status);
                            setRedemptionNotes(redemption.adminNotes || '');
                            setShowRedemptionModal(true);
                          }}
                          className="process-btn"
                        >
                          Process
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="stats-section">
                <h2>Admin Statistics</h2>
                <div className="stats-grid">
                  <div className="stat-card">
                    <h3>Users</h3>
                    <div className="stat-value">{stats.totalUsers}</div>
                    <div className="stat-breakdown">
                      <span>Approved: {stats.approvedUsers}</span>
                      <span>Pending: {stats.pendingUsers}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <h3>Posts</h3>
                    <div className="stat-value">{stats.totalPosts}</div>
                    <div className="stat-breakdown">
                      <span>Approved: {stats.approvedPosts}</span>
                      <span>Pending: {stats.pendingPosts}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <h3>Total Points</h3>
                    <div className="stat-value">{stats.totalPoints}</div>
                  </div>
                  <div className="stat-card">
                    <h3>Bugs/Suggestions</h3>
                    <div className="stat-value">{stats.totalBugs}</div>
                    <div className="stat-breakdown">
                      <span>Open: {stats.openBugs}</span>
                      <span>Resolved: {stats.totalBugs - stats.openBugs}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <h3>Reward Redemptions</h3>
                    <div className="stat-value">{stats.totalRedemptions}</div>
                    <div className="stat-breakdown">
                      <span>Pending: {stats.pendingRedemptions}</span>
                      <span>Processed: {stats.totalRedemptions - stats.pendingRedemptions}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'logs' && (
              <div className="logs-section">
                <h2>Admin Activity Logs</h2>
                {adminActions.length === 0 ? (
                  <p className="empty-state">No admin actions yet</p>
                ) : (
                  <div className="log-list">
                    {adminActions.map(action => (
                      <div key={action.id} className="log-entry">
                        <div className="log-info">
                          <span className="log-admin">{action.adminName}</span>
                          <span className="log-action">{action.actionType.replace(/_/g, ' ')}</span>
                          {action.pointsAllocated && (
                            <span className="log-points">+{action.pointsAllocated} points</span>
                          )}
                        </div>
                        {action.reason && (
                          <p className="log-reason">Reason: {action.reason}</p>
                        )}
                        <p className="log-timestamp">
                          {action.createdAt.toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* User Review Modal */}
      {showUserModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Review User Registration</h2>
            <div className="user-details">
              <p><strong>Name:</strong> {selectedUser.name}</p>
              <p><strong>Roll No:</strong> {selectedUser.rollNo}</p>
              <p><strong>Department:</strong> {selectedUser.department}</p>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p><strong>Joined:</strong> {selectedUser.joinedAt.toLocaleDateString()}</p>
            </div>
            
            <div className="modal-actions">
              <button onClick={handleApproveUser} className="approve-btn">
                Approve User
              </button>
              <div className="reject-section">
                <textarea
                  placeholder="Rejection reason (required for rejection)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="rejection-input"
                />
                <button 
                  onClick={handleRejectUser} 
                  className="reject-btn"
                  disabled={!rejectionReason}
                >
                  Reject User
                </button>
              </div>
              <button onClick={() => setShowUserModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post Review Modal */}
      {showPostModal && selectedPost && (
        <div className="modal-overlay" onClick={() => setShowPostModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Review Post</h2>
            <div className="post-details">
              <p><strong>Title:</strong> {selectedPost.title}</p>
              <p><strong>Author:</strong> {selectedPost.userName}</p>
              <p><strong>Type:</strong> {selectedPost.type}</p>
              <p><strong>Location:</strong> {selectedPost.location}</p>
              <p><strong>Description:</strong> {selectedPost.description}</p>
              <p><strong>Submitted:</strong> {selectedPost.createdAt.toLocaleDateString()}</p>
              <AdminMultiPhotoDisplay key={`${selectedPost.id}-${selectedPost.sessionId}`} post={selectedPost} />
            </div>
            
            {/* AI Analysis Section */}
            <div className="ai-analysis-section" style={{ margin: '1rem 0', padding: '1rem', background: '#f8f9fa', borderRadius: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: '#333' }}>🤖 AI Analysis Assistant</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {aiAnalysis && (
                    <button
                      onClick={() => setShowAiInsights(!showAiInsights)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: showAiInsights ? '#dc3545' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer'
                      }}
                    >
                      {showAiInsights ? 'Hide Analysis' : 'Show Analysis'}
                    </button>
                  )}
                  <button
                    onClick={() => analyzePostWithAI(selectedPost)}
                    disabled={isAnalyzing}
                    style={{
                      padding: '0.5rem 1rem',
                      background: isAnalyzing ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: isAnalyzing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isAnalyzing ? 'Analyzing...' : aiAnalysis ? 'Re-analyze' : 'Analyze with AI'}
                  </button>
                </div>
              </div>
              
              {isAnalyzing && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="spinner"></div>
                  <p>AI is analyzing {selectedPost.sessionId ? 'multiple photo pairs' : 'the photos'}...</p>
                </div>
              )}
              
              {showAiInsights && aiAnalysis && !isAnalyzing && (
                <div className="ai-insights" style={{ marginTop: '1rem' }}>
                  {/* Overall Assessment */}
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#e7f5ff', borderRadius: '0.5rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#0c5460' }}>📊 Overall Assessment</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      <div>
                        <strong>Activity Type:</strong> {aiAnalysis.overallAssessment.activityType}
                      </div>
                      <div>
                        <strong>Authenticity:</strong> 
                        <span style={{ 
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.75rem',
                          background: aiAnalysis.overallAssessment.genuineScore > 80 ? '#28a745' : 
                                      aiAnalysis.overallAssessment.genuineScore > 60 ? '#ffc107' : '#dc3545',
                          color: aiAnalysis.overallAssessment.genuineScore > 60 ? 'white' : 'white',
                          borderRadius: '1rem',
                          fontSize: '0.9rem'
                        }}>
                          {aiAnalysis.overallAssessment.genuineScore}% Genuine
                        </span>
                      </div>
                      <div>
                        <strong>Effort Level:</strong>
                        <span style={{ 
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.75rem',
                          background: aiAnalysis.overallAssessment.effortLevel === 'exceptional' ? '#6f42c1' :
                                      aiAnalysis.overallAssessment.effortLevel === 'high' ? '#28a745' :
                                      aiAnalysis.overallAssessment.effortLevel === 'medium' ? '#ffc107' : '#dc3545',
                          color: 'white',
                          borderRadius: '1rem',
                          fontSize: '0.9rem',
                          textTransform: 'capitalize'
                        }}>
                          {aiAnalysis.overallAssessment.effortLevel}
                        </span>
                      </div>
                      <div>
                        <strong>Impact:</strong>
                        <span style={{ 
                          marginLeft: '0.5rem',
                          textTransform: 'capitalize',
                          color: aiAnalysis.overallAssessment.impactLevel === 'transformative' ? '#6f42c1' :
                                 aiAnalysis.overallAssessment.impactLevel === 'significant' ? '#28a745' :
                                 aiAnalysis.overallAssessment.impactLevel === 'moderate' ? '#17a2b8' : '#6c757d'
                        }}>
                          {aiAnalysis.overallAssessment.impactLevel}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fff', borderRadius: '0.25rem' }}>
                      <strong>🎯 Suggested Points:</strong>
                      <span style={{ marginLeft: '1rem', fontSize: '1.1rem' }}>
                        {aiAnalysis.overallAssessment.suggestedPoints.min} - {aiAnalysis.overallAssessment.suggestedPoints.max}
                      </span>
                      <span style={{ 
                        marginLeft: '1rem',
                        padding: '0.25rem 0.75rem',
                        background: '#007bff',
                        color: 'white',
                        borderRadius: '0.25rem',
                        fontWeight: 'bold'
                      }}>
                        Recommended: {aiAnalysis.overallAssessment.suggestedPoints.recommended}
                      </span>
                    </div>
                  </div>
                  
                  {/* Detailed Analysis */}
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '0.5rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#495057' }}>🔍 Detailed Analysis</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div>
                        <strong>What was done:</strong>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#666' }}>{aiAnalysis.detailedAnalysis.whatWasDone}</p>
                      </div>
                      <div>
                        <strong>Time estimate:</strong> {aiAnalysis.detailedAnalysis.timeEstimate}
                      </div>
                      <div>
                        <strong>Beneficiaries:</strong> {aiAnalysis.detailedAnalysis.beneficiaries}
                      </div>
                      {aiAnalysis.detailedAnalysis.skillsDemonstrated.length > 0 && (
                        <div>
                          <strong>Skills demonstrated:</strong>
                          <div style={{ marginTop: '0.5rem' }}>
                            {aiAnalysis.detailedAnalysis.skillsDemonstrated.map((skill: string, idx: number) => (
                              <span key={idx} style={{
                                display: 'inline-block',
                                margin: '0.25rem',
                                padding: '0.25rem 0.75rem',
                                background: '#e9ecef',
                                borderRadius: '1rem',
                                fontSize: '0.9rem'
                              }}>
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <strong>Sustainability impact:</strong> {aiAnalysis.detailedAnalysis.sustainabilityImpact}
                      </div>
                    </div>
                  </div>
                  
                  {/* Photo Pair Analysis */}
                  {aiAnalysis.photoPairAnalysis.length > 0 && (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fff3cd', borderRadius: '0.5rem' }}>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: '#856404' }}>📸 Photo Analysis</h4>
                      {aiAnalysis.photoPairAnalysis.map((pair: any, idx: number) => (
                        <div key={idx} style={{ 
                          marginBottom: idx < aiAnalysis.photoPairAnalysis.length - 1 ? '1rem' : 0,
                          padding: '0.75rem',
                          background: '#fff',
                          borderRadius: '0.25rem'
                        }}>
                          <strong>Photo Pair {idx + 1}:</strong> {pair.description}
                          {pair.specificChanges.length > 0 && (
                            <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '1rem' }}>
                              {pair.specificChanges.map((change: string, changeIdx: number) => (
                                <li key={changeIdx} style={{ color: '#666' }}>{change}</li>
                              ))}
                            </ul>
                          )}
                          {pair.concerns.length > 0 && (
                            <div style={{ marginTop: '0.5rem', color: '#dc3545' }}>
                              ⚠️ Concerns: {pair.concerns.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Admin Guidance */}
                  <div style={{ 
                    padding: '1rem', 
                    background: aiAnalysis.adminGuidance.recommendation === 'approve' ? '#d4edda' :
                                aiAnalysis.adminGuidance.recommendation === 'reject' ? '#f8d7da' : '#fff3cd',
                    borderRadius: '0.5rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#495057' }}>👨‍💼 Admin Guidance</h4>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ margin: 0, color: '#666' }}>{aiAnalysis.adminGuidance.summary}</p>
                    </div>
                    
                    {aiAnalysis.adminGuidance.positiveAspects.length > 0 && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <strong style={{ color: '#28a745' }}>✅ Positive Aspects:</strong>
                        <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: '1rem' }}>
                          {aiAnalysis.adminGuidance.positiveAspects.map((aspect: string, idx: number) => (
                            <li key={idx} style={{ color: '#155724' }}>{aspect}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {aiAnalysis.adminGuidance.redFlags.length > 0 && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <strong style={{ color: '#dc3545' }}>🚩 Red Flags:</strong>
                        <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: '1rem' }}>
                          {aiAnalysis.adminGuidance.redFlags.map((flag: string, idx: number) => (
                            <li key={idx} style={{ color: '#721c24' }}>{flag}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div style={{ 
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '0.25rem',
                      textAlign: 'center'
                    }}>
                      <strong>AI Recommendation:</strong>
                      <span style={{ 
                        marginLeft: '1rem',
                        padding: '0.5rem 1.5rem',
                        background: aiAnalysis.adminGuidance.recommendation === 'approve' ? '#28a745' :
                                    aiAnalysis.adminGuidance.recommendation === 'reject' ? '#dc3545' : '#ffc107',
                        color: 'white',
                        borderRadius: '0.25rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}>
                        {aiAnalysis.adminGuidance.recommendation}
                      </span>
                    </div>
                    
                    <div style={{ marginTop: '0.75rem', fontStyle: 'italic', color: '#666' }}>
                      <strong>Points Justification:</strong> {aiAnalysis.adminGuidance.pointsJustification}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <div className="approve-section">
                <div className="points-allocation">
                  <label>Allocate Points:</label>
                  <input
                    type="number"
                    value={allocatedPoints}
                    onChange={(e) => setAllocatedPoints(Number(e.target.value))}
                    min="1"
                    max="100"
                    className="points-input"
                  />
                </div>
                <textarea
                  placeholder="Admin comment (optional)"
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  className="comment-input"
                />
                <button 
                  onClick={handleApprovePost} 
                  className="approve-btn"
                  disabled={allocatedPoints <= 0}
                >
                  Approve Post
                </button>
              </div>
              
              <div className="reject-section">
                <textarea
                  placeholder="Rejection reason (required for rejection)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="rejection-input"
                />
                <button 
                  onClick={handleRejectPost} 
                  className="reject-btn"
                  disabled={!rejectionReason}
                >
                  Reject Post
                </button>
              </div>
              
              <button onClick={() => setShowPostModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bug Review Modal */}
      {showBugModal && selectedBug && (
        <div className="modal-overlay" onClick={() => setShowBugModal(false)}>
          <div className="review-modal bug-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Review Bug Report/Suggestion</h2>
            <div className="bug-details">
              <p><strong>Title:</strong> {selectedBug.title}</p>
              <p><strong>Reporter:</strong> {selectedBug.userName}</p>
              <p><strong>Category:</strong> {selectedBug.category}</p>
              <p><strong>Priority:</strong> <span className={`priority-badge priority-${selectedBug.priority}`}>{selectedBug.priority}</span></p>
              <p><strong>Current Status:</strong> <span className={`status-badge status-${selectedBug.status}`}>{selectedBug.status}</span></p>
              <p><strong>Submitted:</strong> {selectedBug.createdAt.toLocaleString()}</p>
              <div className="description-section">
                <strong>Description:</strong>
                <p>{selectedBug.description}</p>
              </div>
            </div>
            
            <div className="modal-actions">
              <div className="status-update">
                <label>Update Status:</label>
                <select
                  value={bugStatus}
                  onChange={(e) => setBugStatus(e.target.value as typeof bugStatus)}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              
              <div className="admin-notes-section">
                <label>Admin Notes/Response:</label>
                <textarea
                  placeholder="Add notes about this bug report or response to the suggestion..."
                  value={bugAdminNotes}
                  onChange={(e) => setBugAdminNotes(e.target.value)}
                  rows={4}
                />
              </div>
              
              <button onClick={handleUpdateBugReport} className="update-btn">
                Update Report
              </button>
              
              <button onClick={() => setShowBugModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redemption Review Modal */}
      {showRedemptionModal && selectedRedemption && (
        <div className="modal-overlay" onClick={() => setShowRedemptionModal(false)}>
          <div className="review-modal redemption-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Process Reward Redemption</h2>
            <div className="redemption-details">
              <p><strong>Reward:</strong> {selectedRedemption.rewardName}</p>
              <p><strong>Points Cost:</strong> {selectedRedemption.pointsCost} points</p>
              <p><strong>User:</strong> {selectedRedemption.userName} ({selectedRedemption.userRollNo})</p>
              <p><strong>WhatsApp:</strong> <a href={`https://wa.me/${selectedRedemption.whatsappNumber.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer">{selectedRedemption.whatsappNumber}</a></p>
              <p><strong>Current Status:</strong> <span className={`status-badge status-${selectedRedemption.status}`}>{selectedRedemption.status}</span></p>
              <p><strong>Requested:</strong> {selectedRedemption.createdAt.toLocaleString()}</p>
              {selectedRedemption.processedAt && (
                <p><strong>Last Processed:</strong> {selectedRedemption.processedAt.toLocaleString()}</p>
              )}
            </div>
            
            <div className="modal-actions">
              <div className="status-update">
                <label>Update Status:</label>
                <select
                  value={redemptionStatus}
                  onChange={(e) => setRedemptionStatus(e.target.value as typeof redemptionStatus)}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="delivered">Delivered</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="admin-notes-section">
                <label>Admin Notes:</label>
                <textarea
                  placeholder="Add notes about this redemption (e.g., delivery details, rejection reason)..."
                  value={redemptionNotes}
                  onChange={(e) => setRedemptionNotes(e.target.value)}
                  rows={4}
                />
              </div>
              
              <button onClick={handleProcessRedemption} className="update-btn">
                Update Redemption
              </button>
              
              <button onClick={() => setShowRedemptionModal(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;